import { useMutation } from "@apollo/client";
import moment from "moment";
import { useCallback, useMemo } from "react";
import {
  AssessmentGroupAnonymity,
  AssessmentState,
  AssessmentType,
  GetAssessmentQuery,
  UnsubmitAssessmentMutation,
  UnsubmitAssessmentMutationVariables,
} from "types/graphql-schema";

import getAssessmentQuery from "@apps/assessments/graphql/get-assessment-query";
import reopenAssessmentMutation from "@apps/assessments/graphql/reopen-assessment-mutation";
import useLabel from "@apps/use-label/use-label";
import {
  currentUserVar,
  isAdminVar,
  successNotificationVar,
} from "@cache/cache";
import Avatar from "@components/avatar/avatar";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import { useLink } from "@components/link/link-helpers";
import Loading from "@components/loading/loading";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { getAssessmentTypeLabel } from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import RelatedSubmissions from "./related-submissions";

const AssessmentHeader = ({
  myAssessmentsData,
}: {
  myAssessmentsData?: GetAssessmentQuery;
}) => {
  const currentUser = currentUserVar();
  const isAdmin = isAdminVar();
  const label = useLabel();
  const link = useLink();

  const assessment = useMemo(
    () => (myAssessmentsData ? myAssessmentsData.assessment : null),
    [myAssessmentsData],
  );
  const complianceProgram = useMemo(
    () => (assessment ? assertNonNull(assessment.complianceProgram) : null),
    [assessment],
  );
  const canReadProgramDetails = useMemo(
    () => complianceProgram?.canReadDetails.permission,
    [complianceProgram],
  );

  const isEngagementSurvey = useMemo(
    () =>
      assessment?.template.assessmentType === AssessmentType.EngagementSurvey,
    [assessment],
  );
  const target = useMemo(
    () =>
      !isEngagementSurvey && assessment
        ? assertNonNull(assessment.target)
        : null,
    [assessment, isEngagementSurvey],
  );
  const responder = useMemo(() => assessment?.responder ?? null, [assessment]);
  const isSelfAssessment = useMemo(
    () => target?.id === responder?.id,
    [responder, target],
  );
  const assessmentLabel = useMemo(() => {
    if (isEngagementSurvey) {
      return "Survey";
    }
    return "review";
  }, [isEngagementSurvey]);

  const getHeaderTitle = useMemo(() => {
    if (isEngagementSurvey) {
      return label(assessmentLabel, { capitalize: true });
    }

    const reviewTypeLabel = getAssessmentTypeLabel(
      assertNonNull(assessment?.template.assessmentType),
      isSelfAssessment,
    );

    return `${reviewTypeLabel} ${label("review", { capitalize: true })}: ${
      target?.name
    }`;
  }, [
    isEngagementSurvey,
    assessment?.template.assessmentType,
    target?.name,
    isSelfAssessment,
    label,
    assessmentLabel,
  ]);

  const [reopenAssessment, { loading: isReopeningAssessment }] = useMutation<
    UnsubmitAssessmentMutation,
    UnsubmitAssessmentMutationVariables
  >(reopenAssessmentMutation);

  const handleReopenAssessment = useCallback(() => {
    if (!assessment) {
      return;
    }
    const isResponder = currentUser.id === assessment.responder?.id;
    reopenAssessment({
      variables: {
        assessmentId: assessment.id,
      },
      refetchQueries: isResponder ? [getAssessmentQuery] : [],
      onCompleted: () => {
        successNotificationVar({
          title: `${label(assessmentLabel, {
            capitalize: true,
          })} reopened. ${isResponder ? "You" : "The responder"} can now edit ${
            isResponder ? "your" : "their"
          } responses.`,
        });
        if (!isResponder) {
          link.redirect("/assessments");
        }
      },
    });
  }, [
    assessment,
    currentUser.id,
    reopenAssessment,
    label,
    assessmentLabel,
    link,
  ]);

  if (!assessment) {
    return null;
  }

  const leftCellClassName = "py-2 font-medium w-[200px]";
  const rightCellClassName = "py-2";

  return (
    <div className={classNames("mb-12 text-sm pb-6 border-b")}>
      <div className="flex flex-col gap-6 w-full mx-auto max-w-screen-lg">
        <div className="flex items-center gap-2">
          {!isEngagementSurvey && <Avatar user={target} size={8} />}
          <h2 className="text-2xl font-bold text-gray-800">{getHeaderTitle}</h2>
        </div>

        <div>
          <Layout.MainSubSection
            title={`${label(assessmentLabel, { capitalize: true })} Details`}
            collapsible
            defaultIsExpanded
          >
            <table className="text-gray-600 w-full">
              <tbody>
                <tr>
                  <td className={leftCellClassName}>Program</td>
                  {canReadProgramDetails ? (
                    <td
                      className={classNames(
                        rightCellClassName,
                        "py-0", // to compensate for button padding
                      )}
                    >
                      <Button
                        to={`/programs/${complianceProgram?.id}`}
                        theme={buttonTheme.text}
                        leftNegativeMargin
                      >
                        <TopicflowIcon
                          type="program"
                          className="shrink-0 w-4 h-4"
                        />
                        {complianceProgram?.title}
                      </Button>
                    </td>
                  ) : (
                    <td
                      className={classNames(
                        rightCellClassName,
                        "flex items-center gap-1",
                      )}
                    >
                      <TopicflowIcon
                        type="program"
                        className="shrink-0 w-4 h-4"
                      />
                      {complianceProgram?.title}
                    </td>
                  )}
                </tr>

                {!isEngagementSurvey && isSelfAssessment && (
                  <tr>
                    <td className={leftCellClassName}>Subject</td>
                    <td className={rightCellClassName}>
                      <div className="flex items-center">
                        <Avatar
                          className="mr-1"
                          user={assertNonNull(target)}
                          size="5"
                        />{" "}
                        {`${target?.name} (Self ${label("review", {
                          capitalize: true,
                        })})`}
                      </div>
                    </td>
                  </tr>
                )}

                {isEngagementSurvey && (
                  <tr>
                    <td className={leftCellClassName}>Responder</td>
                    <td className={rightCellClassName}>
                      <div className="flex items-center">
                        <Avatar className="mr-1" user={responder} size="5" />{" "}
                        {responder?.name ?? "Anonymous"}
                      </div>
                    </td>
                  </tr>
                )}

                {!isEngagementSurvey && !isSelfAssessment && (
                  <>
                    <tr>
                      <td className={leftCellClassName}>Responder</td>
                      <td className={rightCellClassName}>
                        <div className="flex items-center">
                          <Avatar className="mr-1" user={responder} size="5" />{" "}
                          {responder?.name ?? "Anonymous"}
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <td className={leftCellClassName}>Subject</td>
                      <td className={rightCellClassName}>
                        <div className="flex items-center">
                          <Avatar
                            className="mr-1"
                            user={assertNonNull(target)}
                            size="5"
                          />{" "}
                          {target?.name}
                        </div>
                      </td>
                    </tr>
                    {target?.organizationSpecificInfo?.startDate && (
                      <tr>
                        <td className={leftCellClassName}>
                          Subject's Start Date
                        </td>
                        <td className={rightCellClassName}>
                          {moment(
                            target?.organizationSpecificInfo?.startDate,
                          ).format("MMM D, YYYY")}
                        </td>
                      </tr>
                    )}
                    {assessment.targetPosition && (
                      <tr>
                        <td className={leftCellClassName}>Position</td>
                        <td className={rightCellClassName}>
                          {assessment.targetPosition}
                        </td>
                      </tr>
                    )}
                    {target?.organizationSpecificInfo?.jobLevel && (
                      <tr>
                        <td className={leftCellClassName}>Job level</td>
                        <td className={rightCellClassName}>
                          {target?.organizationSpecificInfo?.jobLevel}
                        </td>
                      </tr>
                    )}
                  </>
                )}

                {!isEngagementSurvey && (
                  <tr>
                    <td className={leftCellClassName}>Program period</td>
                    <td className={rightCellClassName}>
                      {assessment?.periodStartDate && assessment?.periodEndDate
                        ? `${moment(assessment.periodStartDate).format(
                            "MMM D, YYYY",
                          )} - ${moment(assessment.periodEndDate).format(
                            "MMM D, YYYY",
                          )}`
                        : "Not set"}
                    </td>
                  </tr>
                )}
                <tr>
                  <td className={leftCellClassName}>Due date</td>
                  <td className={rightCellClassName}>
                    {moment(assessment?.dueDate).format("MMM D, YYYY")}
                  </td>
                </tr>
                {assessment.state === AssessmentState.Submitted && (
                  <tr>
                    <td className={leftCellClassName}>Submitted</td>
                    <td className={rightCellClassName}>
                      <div className="text-green-700">
                        {moment(assessment.submittedDatetime).format(
                          "MMM D, YYYY @ h:mma",
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Layout.MainSubSection>

          {!isEngagementSurvey &&
            assessment.target &&
            assessment.complianceProgram && (
              <RelatedSubmissions
                assessmentState={assessment.state}
                assessmentType={assessment.template.assessmentType}
                complianceProgramId={assessment.complianceProgram.id}
                target={assessment.target}
              />
            )}
        </div>

        {assessment.state === AssessmentState.Submitted && isAdmin && (
          <div className="flex items-center w-full gap-2">
            <Button
              className="mr-2"
              onClick={handleReopenAssessment}
              disabled={isReopeningAssessment}
              text="Reopen"
            />
            {isReopeningAssessment && <Loading size="5" mini />}
          </div>
        )}

        {[
          AssessmentGroupAnonymity.SemiAnonymous,
          AssessmentGroupAnonymity.Anonymous,
        ].includes(assessment.template.anonymity) &&
          assessment.state !== AssessmentState.Submitted &&
          currentUser.id === responder?.id &&
          !isSelfAssessment && (
            <div className="border rounded-md border-gray-200 text-gray-500 py-3 px-4">
              <TopicflowIcon
                type="lockClosed"
                className="h-4 w-4 inline mr-1"
              />{" "}
              Your responses will appear anonymous
              {assessment.template.anonymity ===
              AssessmentGroupAnonymity.SemiAnonymous
                ? " to the subject"
                : ""}
              .{" "}
              {isEngagementSurvey
                ? "They will be shared with your upward reporting line and admins. "
                : ""}
              Anonymity will be affected if you put identifiable content in your
              comments.
            </div>
          )}
      </div>
    </div>
  );
};

export default AssessmentHeader;
